/* DropZone.jsx
 * -----------------------------------------------------------------------------
 * Standard LEFT / RIGHT / ABOVE / BELOW zones  +  EMPTY canvas zone.
 * 2025-05-01  “display:none eats drop-targets” fix   →  zones always in DOM
 * 2025-05-02  “giant gaps” patch                    →  width/height 0 ➜ 8 px
 */

import React, { useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { DropZoneTypes } from './constants';
import { useFormLayout } from '../../../contexts/FormLayoutContext';

/** Special variant shown when there are zero rows */
export const EMPTY_DROPZONE = 'EMPTY';

/* How thick a live drop-zone should appear (px) */
const DROP_SIZE = 8;          // <— change this to taste (6-10px feels nice)

const DropZone = ({
  onDrop,
  type,
  rowIndex,
  fieldIndex,
  alwaysVisible = false,
}) => {
//  const { showDropZones } = useFormLayout();

  /* Default indices (preserve original fallback logic) */
  rowIndex   = rowIndex   ?? 0;
  fieldIndex = fieldIndex ?? 0;

  /*────────────────────────────── React-DnD hook ───────────────────────────*/
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'field',
    drop: (item) => {
      let adjustedTargetRowIndex = rowIndex;
      let adjustedFieldIndex     = fieldIndex;

      switch (type) {
        case EMPTY_DROPZONE:                  // large empty canvas zone
        case DropZoneTypes.ABOVE:
          adjustedTargetRowIndex = 0;
          adjustedFieldIndex     = 0;
          break;

        case DropZoneTypes.BELOW:
          adjustedTargetRowIndex = rowIndex + 1;
          adjustedFieldIndex     = 0;
          break;

        case DropZoneTypes.LEFT:
          adjustedFieldIndex     = 0;
          break;

        case DropZoneTypes.RIGHT:
          // IMPORTANT:
          // RowComponent passes `fieldIndex` for RIGHT as the *insertion index* already
          // (it uses fieldIndex={fieldIndex + 1}).
          // So we must NOT add +1 here or we will skip a slot and misplace inserts.
          adjustedFieldIndex     = fieldIndex;
          break;


        default:
          break;
      }

      /* -------------- original undefined-check preserved ------------------ */
      if (
        adjustedTargetRowIndex === undefined ||
        adjustedFieldIndex     === undefined
      ) {
        console.error('[DropZone] Error: Undefined adjusted indices', {
          originalRowIndex   : rowIndex,
          originalFieldIndex : fieldIndex,
          adjustedTargetRowIndex,
          adjustedFieldIndex,
        });
        return;
      }

      /* Translate EMPTY → ABOVE so upstream logic stays unchanged */
      const effectiveType =
        type === EMPTY_DROPZONE ? DropZoneTypes.ABOVE : type;

      // Use the DRAGGED ITEM as the source of truth for original indices when available.
      // `rowIndex` and `fieldIndex` in this component represent the DROP ZONE target,
      // not where the item came from.
      //
      // IMPORTANT:
      // - Existing canvas drags MUST include item.rowIndex + item.index.
      // - New palette items (isNew=true) do NOT have an origin rowIndex, because they
      //   were not dragged from the canvas. Those are allowed to omit origin indices.
      const isNewItem = !!item?.isNew;

      const originalRowIndexFromItem = isNewItem ? -1 : item?.rowIndex;
      const originalFieldIndexFromItem = isNewItem ? -1 : item?.index;

      // Preserve original undefined-check style (but now validate origin ONLY for existing items)
      if (
        !isNewItem &&
        (originalRowIndexFromItem === undefined ||
         originalFieldIndexFromItem === undefined)
      ) {
        console.error('[DropZone] Error: Drag item missing origin indices', {
          item,
          effectiveType,
          dropZoneRowIndex: rowIndex,
          dropZoneFieldIndex: fieldIndex,
          adjustedTargetRowIndex,
          adjustedFieldIndex,
        });
        return;
      }

      console.log('[DropZone] drop', {
        type,
        effectiveType,
        isNewItem,
        dropZoneRowIndex: rowIndex,
        dropZoneFieldIndex: fieldIndex,
        originalRowIndexFromItem,
        originalFieldIndexFromItem,
        adjustedTargetRowIndex,
        adjustedFieldIndex,
      });

      onDrop(
        item,
        effectiveType,
        originalRowIndexFromItem,
        originalFieldIndexFromItem,
        adjustedTargetRowIndex,
        adjustedFieldIndex
      );


    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  /* Optional debug */
  useEffect(() => {
    // console.log('[DropZone] mount', { type, rowIndex, fieldIndex });
  }, [type, rowIndex, fieldIndex]);

  /*────────────────────────────── styling ────────────────────────────────*/

    /* Zones stay in the DOM but “collapse” when disarmed */
const zoneIsArmed = alwaysVisible || canDrop || isOver;
  //const zoneIsArmed = true; // TEMP DIAG: if internal reorders now work, arming is the bug
  // DEBUG (low-noise): confirm whether arming is flickering per zone render
  // console.log('[DropZone] armed?', { type, rowIndex, fieldIndex, zoneIsArmed });

  const base = {
    alignSelf       : 'center',
    position        : 'relative',

    // ENTERPRISE:
    // DropZones must sit ABOVE field surfaces when using overlap margins,
    // otherwise fields (HoveredField zIndex:12, drag bar zIndex:20) will intercept drop.
    zIndex          : zoneIsArmed ? 1000 : 2,

    justifyContent  : 'center',
    alignItems      : 'center',
    transition      : 'none',
    color           : 'white',
    fontSize        : '0.75rem',
    display         : 'flex',
    opacity         : zoneIsArmed ? 1 : 0,
    pointerEvents   : zoneIsArmed ? 'auto' : 'none',
  };

  const style =
    type === EMPTY_DROPZONE
      ? {
          ...base,
          height : '180px',
          width  : '100%',
          border : '2px dashed rgba(25,118,210,0.7)',
          borderRadius : 6,
          backgroundColor: isOver
            ? 'rgba(25,118,210,0.12)'
            : 'rgba(25,118,210,0.04)',
          color    : 'rgba(25,118,210,0.7)',
          fontSize : '1.1rem',
          textAlign: 'center',
          /* EMPTY zone is always visible & interactive */
          opacity        : 1,
          pointerEvents  : 'auto',
        }
      : {
          ...base,

          // ENTERPRISE: keep drop-zone geometry stable at all times.
          // We NEVER collapse to 0px during arming/disarming because HTML5 drag
          // can be cancelled by layout reflow at dragstart.
          height:
            type === DropZoneTypes.LEFT || type === DropZoneTypes.RIGHT
              ? '100%'
              : `${DROP_SIZE}px`,

          minHeight:
            type === DropZoneTypes.LEFT || type === DropZoneTypes.RIGHT
              ? '70px'
              : 'auto',

          width:
            type === DropZoneTypes.LEFT || type === DropZoneTypes.RIGHT
              ? `${DROP_SIZE}px`
              : '100%',

          // ENTERPRISE: avoid permanent gutters by overlapping the hitbox.
          // These margins are CONSTANT (never change on drag), so still no reflow.
          marginTop:
            type === DropZoneTypes.BELOW ? `-${DROP_SIZE}px` : 0,

          marginBottom:
            type === DropZoneTypes.ABOVE ? `-${DROP_SIZE}px` : 0,

          marginLeft:
            type === DropZoneTypes.RIGHT ? `-${DROP_SIZE}px` : 0,

          marginRight:
            type === DropZoneTypes.LEFT ? `-${DROP_SIZE}px` : 0,

          // Optional perf hint (paint-only changes on hover/opacity)
          willChange: 'opacity, background-color',

          backgroundColor: isOver
            ? '#1976d2'
            : 'rgba(25,118,210,0.5)',
        };

  /*────────────────────────────── render ────────────────────────────────*/
  return (
    <div ref={drop} style={style}>
      {type === EMPTY_DROPZONE &&
        'Drag fields here to start designing your form'}
    </div>
  );
};

export default DropZone;
